import React, { useEffect, useRef, useState } from "react";
import { Card } from "@material-tailwind/react";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";

import LoadingOverlay from "../../LoadingOverlay";
import { loginSignupController } from "./LoginSignupController";
import { SignupCard } from "./Signup/Signup";
import { LoginCard } from "./Login/Login";
import { loginController } from "./Login/LoginController";
import { signupController } from "./Signup/SignupController";
import { alertController } from "../../../Controllers/AlertController";

export const LoginSignupCard = observer(() => {
  const localRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (loginSignupController.windowVisible) {
      setIsVisible(true);
    } else {
      const timeout = setTimeout(() => setIsVisible(false), 300);
      return () => clearTimeout(timeout);
    }
  }, [loginSignupController.windowVisible]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as HTMLElement;

      // Ignore clicks on alerts
      if (
        target.closest('.alert-root') || target.closest('.password-reset-dialog')
      ) {
        return;
      }

      if (localRef.current && !localRef.current.contains(target)) {
        loginSignupController.setLoginSignupWindowVisible(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    async function handleKeyDown(e: KeyboardEvent) {
      if (e.key !== "Enter") return;

      // Only handle when popup is visible
      if (!loginSignupController.windowVisible) return;

      e.preventDefault();

      if (loginSignupController.currentWindow === "LOGIN") {
        if (!loginController.username || !loginController.password) return;
        const loginSuccessful = await loginController.login()
        if (!loginSuccessful) {
          alertController.displayAlert(
            "Credentials incorrect",
            "Either username or password are incorrect, please try again",
            "error",
            4000
          )
          return;
        }
      } else if (loginSignupController.currentWindow === "SIGNUP") {
        if (signupController.step === 1 && !signupController.step2Disabled) {
          const validation = await signupController.validateFirstStep();

          if (!!validation && !validation.isValid) {
            alertController.displayAlert(
              "Validation failed",
              validation.errors.map(e => e.errorMessage),
              "error",
              4000
            )
            return;
          }

          if (!signupController.companyName || signupController.companyName === "")
            signupController.companyName = signupController.stockistName;
          signupController.nextStep();
        } else if (signupController.step === 2 && !signupController.signupDisabled) {
          const validation = await signupController.validateUser();

          if (!!validation && !validation.isValid) {
            alertController.displayAlert(
              "Signup failed",
              validation.errors.map(e => e.errorMessage),
              "error",
              4000
            )
            return;
          }

          const apiUser = await signupController.signUp()
          if (apiUser) {
            alertController.displayAlert(
              "Signup successful",
              "We are reviewing your signup request, and will contact you once your account has been activated. In the meantime, please feel free to use your account to browse and add items to your basket.",
              "success"
            )
            navigate("/latest")
            return;
          }
          alertController.displayAlert(
            "Signup failed",
            "Please check your details and try again",
            "error",
            4000
          )
        }
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  if (!isVisible) return null;

  const isClosing = !loginSignupController.windowVisible;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black bg-opacity-50" />
      <Card
        ref={localRef}
        className={`relative z-10 w-full smh:w-[90vw] h-[100dvh] smh:h-auto max-w-sm max-h-[780px] transform rounded-none smh:rounded-xl shadow-2xl shadow-light-grey transition ${isClosing ? "animate-fadeOut" : "animate-fadeIn"
          }`}
      >
        <LoginCard visible={loginSignupController.currentWindow === "LOGIN"} />
        <SignupCard visible={loginSignupController.currentWindow === "SIGNUP"} />
        <LoadingOverlay isVisible={loginSignupController.isLoading} isFixed={false} />
      </Card>
    </div>
  );
});